/**
 * Custom hooks for HS Codes API
 *
 * Provides hooks for searching Harmonized System classification codes
 */
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

// ============================================================================
// TYPES
// ============================================================================

export interface HSCodeItem {
  id: number;
  code: string;
  hs_version: string;
  level: number;
  parent_code: string | null;
  section: string | null;
  description_en: string;
}

export interface HSCodeWithHierarchy {
  code: HSCodeItem;
  hierarchy: HSCodeItem[];
}

// ============================================================================
// QUERY KEYS
// ============================================================================

export const HS_CODES_QUERY_KEYS = {
  all: ["hs-codes"] as const,
  search: (query: string, level?: number) =>
    [...HS_CODES_QUERY_KEYS.all, "search", query, level] as const,
  byCode: (code: string) => [...HS_CODES_QUERY_KEYS.all, code] as const,
  children: (code: string) => [...HS_CODES_QUERY_KEYS.all, code, "children"] as const,
};

// ============================================================================
// SEARCH HS CODES HOOK
// ============================================================================

/**
 * Hook to search HS codes for autocomplete
 *
 * @param query - Search term in description (e.g., "avocado", "coffee")
 * @param level - Optional level filter (2=Chapter, 4=Heading, 6=Subheading)
 * @param enabled - Whether the query should be enabled
 */
export function useHSCodes(
  query: string,
  level?: number,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: HS_CODES_QUERY_KEYS.search(query, level),
    queryFn: async (): Promise<HSCodeItem[]> => {
      const params: Record<string, string | number> = {
        limit: 50,
      };
      if (query) params.q = query;
      if (level) params.level = level;

      return await apiGet<HSCodeItem[]>("/hs-codes", { params });
    },
    enabled: enabled && query.length >= 2,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// ============================================================================
// GET HS CODE WITH HIERARCHY HOOK
// ============================================================================

/**
 * Hook to fetch an HS code with its full parent hierarchy
 *
 * @param code - HS code (2, 4, or 6 digits)
 * @param enabled - Whether the query should be enabled
 */
export function useHSCodeByCode(code: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: HS_CODES_QUERY_KEYS.byCode(code || ""),
    queryFn: async (): Promise<HSCodeWithHierarchy> => {
      if (!code) throw new Error("No HS code provided");
      return await apiGet<HSCodeWithHierarchy>(`/hs-codes/${code}`);
    },
    enabled: enabled && !!code,
    staleTime: Infinity, // HS codes are static
  });
}

// ============================================================================
// GET HS CODE CHILDREN HOOK
// ============================================================================

/**
 * Hook to fetch children (sub-categories) of an HS code
 *
 * @param code - Parent HS code
 * @param enabled - Whether the query should be enabled
 */
export function useHSCodeChildren(code: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: HS_CODES_QUERY_KEYS.children(code || ""),
    queryFn: async (): Promise<HSCodeItem[]> => {
      if (!code) throw new Error("No HS code provided");
      return await apiGet<HSCodeItem[]>(`/hs-codes/${code}/children`);
    },
    enabled: enabled && !!code,
    staleTime: Infinity,
  });
}
